import React, {useEffect, useState} from "react";
import CardBase from "connectui/component/display/card/CardBase";
import SvgIcon from "connectui/component/display/SvgIcon";
import Text from "connectui/component/display/Text";
import ButtonText from "connectui/component/input/button/ButtonText";
import LightButtonBase from "connectui/component/input/button/base/LightButtonBase";
import {ReactComponent as WhatsAppIcon} from "connectui/icons/WhatsApp-card.svg";
import {messagingSettingsStageEnum} from "ui/component/businesssettings/messaging/BaseMessagingSettingsPanel";
import {useDispatch, useSelector} from "react-redux";
import FlexContainer from "connectui/component/layout/FlexContainer";
import StatusChip from "../chips/StatusChip";
import {useTheme} from "connectui/theme/ThemeProvider";
import Tooltip from "connectui/component/display/Tooltip";
import {ReactComponent as WhatsAppMiniIcon} from "connectui/icons/whatsapp-mini.svg";
import {getServiceAccounts} from "redux/actions/servicesActions";
import Skeleton from "connectui/component/display/Skeleton";
import OutlinedButtonBase from "../../../../../../connectui/component/input/button/base/OutlinedButtonBase";
import ServiceInfoDialog from "../../../../../../components/Dialogs/ServiceInfoContent";
import {addonServices} from "../../../../../../configuration/enums";
import {postChannels} from "../../../../../../redux/actions/accountsActions";

const WhatsAppCard = ({setStage, data, ...props}) => {
  const {theme} = useTheme();
  const dispatch = useDispatch();

  const [getServiceAccountsError, setGetServiceAccountsError] = useState(null);

  useEffect(() => {
    dispatch(
      getServiceAccounts({
        onSuccess: (response) => {
          setGetServiceAccountsError(null);
        },
        onError: () => {
          setGetServiceAccountsError(
            "Error getting service account details, please try again later.",
          );
        },
      }),
    );
  }, [dispatch]);

  const serviceAccountsLoading = useSelector(
    (state) => state.defaultReducer.loading.service_accounts,
  );

  const twilioServiceAccount = useSelector(
    (state) => state.defaultReducer.service_accounts?.accounts?.twilio?.[0],
  );
  const serviceAccountExists =
    !!twilioServiceAccount &&
    Object.keys(twilioServiceAccount).length > 0 &&
    twilioServiceAccount.properties?.["whatsapp_sender_id"];

  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );

  const userProfileLoading = useSelector(
    (state) => state.defaultReducer.loading.profile,
  );
  const isSmsEnabled = user_profile?.connected_services?.includes("sms");
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const subscribe = () => {
    setSubscriptionLoading(true);
    dispatch(
      postChannels(
        {
          enso_key: user_profile?.enso_key,
          data: {service: "sms", provider: "twilio", options: {}},
        },
        (response) => {
          if (response.options.success) {
            user_profile.connected_services = [
              ...(user_profile.connected_services || []),
              "sms",
            ];
          } else if (response.options.message === "pay") {
            window.location.href = "/admin/settings/billing/:redirect";
          }
          setSubscriptionLoading(false);
          setSubscribeDialogOpen(false);
        },
        () => {
          setSubscriptionLoading(false);
          setSubscribeDialogOpen(false);
        },
      ),
    );
  };

  // Assuming this is how we fetch the numbers
  const whatsAppNumbers =
    user_profile?.managed_phones?.filter(
      (p) =>
        p.active && p.capabilities?.some((c) => c.toLowerCase() === "whatsapp"),
    ) || [];

  const ManagedPhoneItem = ({phone, theme, setStage}) => (
    <FlexContainer alignItems="center" gap={theme.spacing(2)}>
      <SvgIcon component={WhatsAppMiniIcon} sx={{width: 16, height: 16}} />
      <Text variant="textMD" sx={{paddingRight: theme.spacing(1)}}>
        {phone.phone}
      </Text>
      <Tooltip
        tooltipContent={
          <Text variant="textXS">
            {phone.active
              ? "This number is active."
              : "This number is not active."}
          </Text>
        }
      >
        <StatusChip
          text={phone.active ? "Active" : "Not Active"}
          type={phone.active ? "success" : "blue"}
        />
      </Tooltip>
    </FlexContainer>
  );

  const getCardDescription = () => {
    return (
      <Text
        skeleton={{
          enabled: serviceAccountsLoading,
          width: "400px",
          height: "25px",
        }}
        variant="textSM"
        sx={{color: theme.colors.textSecondary}}
      >
        {serviceAccountExists
          ? "Your WhatsApp Business Account is connected."
          : "Connect your WhatsApp Business account to communicate with your guests directly through this channel."}
      </Text>
    );
  };

  const getCardContent = () => {
    return (
      <FlexContainer
        skeleton={{
          enabled: serviceAccountsLoading,
          width: "220px",
          height: "25px",
        }}
        flexDirection="column"
        alignItems="flex-start"
        gap={theme.spacing(3)}
      >
        {whatsAppNumbers.map((p) => (
          <FlexContainer
            flexDirection="column"
            key={p.phone}
            gap={theme.spacing(1.5)}
          >
            <ManagedPhoneItem phone={p} theme={theme} setStage={setStage} />
          </FlexContainer>
        ))}
      </FlexContainer>
    );
  };

  const getActionRow = () => {
    if (serviceAccountExists) {
      return [
        <ButtonText
          skeleton={{
            enabled: serviceAccountsLoading,
            width: "150px",
            height: "36px",
          }}
          sx={{flex: 1}}
          typographyVariant="textSM"
          weight="semibold"
          color="primary"
          onClick={() =>
            setStage({
              id: messagingSettingsStageEnum.whatsAppEdit,
              data: {},
            })
          }
          buttonBase={LightButtonBase}
          label="Manage Account"
        />,
      ];
    }

    return [
      <ButtonText
        skeleton={{
          enabled: serviceAccountsLoading,
          width: "150px",
          height: "36px",
        }}
        sx={{flex: 1}}
        typographyVariant="textSM"
        weight="semibold"
        color="primary"
        onClick={() =>
          setStage({
            id: messagingSettingsStageEnum.whatsApp,
            data: {},
          })
        }
        buttonBase={LightButtonBase}
        label="Connect Number"
      />,
    ];
  };

  const getStatusChip = () => {
    if (serviceAccountsLoading) {
      return <Skeleton width={"150px"} />;
    }

    if (!serviceAccountExists) {
      return <StatusChip text={"Not Enabled"} type={"error"} />;
    }

    const whatsAppStatus =
      twilioServiceAccount?.properties?.["whatsapp_status"];
    // TODO: Need a default case if status not available
    return (
      <StatusChip
        text={whatsAppStatus}
        type={
          {
            pending: "warning",
            approved: "success",
            rejected: "error",
            creating: "gray",
            verifying: "blue",
            offline: "error",
            online: "success",
            pending_verification: "warning",
          }[whatsAppStatus?.toLowerCase()] || "gray"
        }
      />
    );
  };

  if (!!getServiceAccountsError) {
    return (
      <CardBase
        icon={<SvgIcon component={WhatsAppIcon} />}
        title={
          <FlexContainer alignItems="center" gap={theme.spacing(3)}>
            <Text variant="textLG">WhatsApp</Text>
          </FlexContainer>
        }
        description={
          <Text weight="medium" color="error500">
            {getServiceAccountsError}
          </Text>
        }
        {...props}
      />
    );
  }

  if (!isSmsEnabled && !userProfileLoading) {
    return (
      <>
        <CardBase
          icon={<SvgIcon component={WhatsAppIcon} />}
          title={
            <FlexContainer alignItems="center" gap={theme.spacing(3)}>
              <Text variant="textLG">WhatsApp</Text>
            </FlexContainer>
          }
          actionRow={
            <ButtonText
              sx={{flex: 1}}
              typographyVariant="textSM"
              weight="semibold"
              color="gray700"
              onClick={() => setSubscribeDialogOpen(true)}
              buttonBase={OutlinedButtonBase}
              label="Enable SMS"
            />
          }
          description={
            <Text
              skeleton={{
                enabled: userProfileLoading,
              }}
              weight="medium"
              color="warning500"
            >
              {"Please enable SMS before using WhatsApp"}
            </Text>
          }
          {...props}
        />
        <ServiceInfoDialog
          open={subscribeDialogOpen}
          service={addonServices.twilio[0]}
          handleEnable={subscribe}
          loading={subscriptionLoading}
          onClose={() => setSubscribeDialogOpen(false)}
          btnLabel="Subscribe"
        />
      </>
    );
  }

  return (
    <CardBase
      icon={<SvgIcon component={WhatsAppIcon} />}
      title={
        <FlexContainer alignItems="center" gap={theme.spacing(3)}>
          <Text variant="textLG">WhatsApp</Text>
          {getStatusChip()}
        </FlexContainer>
      }
      description={getCardDescription()}
      inlineContent={getCardContent()}
      actionRow={getActionRow()}
      {...props}
    />
  );
};

export default WhatsAppCard;
